import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/useAuthStore';
import { refresh } from '@/api/auth.api';

/**
 * Schützt alle Kind-Routen.
 * Versucht beim Mount einen Silent-Refresh via Cookie.
 * Leitet bei Fehlschlag auf /login um.
 */
export function AuthGuard() {
  const { isAuthenticated, isLoading, setAuth, clearAuth, setLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function tryRefresh() {
      try {
        const { accessToken, csrfToken, user } = await refresh();
        if (!cancelled) {
          setAuth(accessToken, user, csrfToken);
          // Query-Cache leeren damit mit neuem Token frisch geladen wird
          queryClient.removeQueries();
          if (user.mustChangePassword) {
            navigate('/change-password', { replace: true });
          }
        }
      } catch {
        if (!cancelled) {
          clearAuth();
          // Ziel-URL merken, damit der Login danach dorthin zurückführt
          // (Deep-Links / Lesezeichen gehen sonst verloren).
          navigate('/login', {
            replace: true,
            state: { from: { pathname: location.pathname + location.search } },
          });
        }
      }
    }

    tryRefresh();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, setAuth, clearAuth, setLoading, queryClient]);

  if (isLoading && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
          <p className="mt-3 text-sm text-slate-500">Sitzung wird geprüft...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <Outlet />;
}
